import type { Model } from "@tento-core/model";

import type { Json } from "../core/json.ts";
import { ShapeKey } from "../core/shape.ts";

/**
 * A wire cell shape: the generated model that hydrates a cell's wire JSON
 * into `C`. Both members take the WIRE value; a `Shape` probes the
 * canonical value a decoded cell holds.
 */
export interface CellShape<C> {
  readonly key: ShapeKey<C>;
  readonly encoding: string;
  readonly model: Model<C>;
  /** Hydrate a cell's wire JSON into `C`, or say why it does not fit. */
  safeHydrate(raw: Json): Model.Result<C>;
  /**
   * Whether a cell's wire JSON hydrates as this shape. Not a type guard:
   * hydration rewrites fields (an ISO string becomes a `DateTime`), so the
   * wire value itself is never a `C` — `safeHydrate` returns the `C`.
   */
  probe(value: Json): boolean;
}

export namespace CellShape {
  export function model<C>(args: {
    readonly id: string;
    readonly encoding: string;
    readonly model: Model<C>;
  }): CellShape<C> {
    const key = new ShapeKey<C>(args.id);
    return {
      key,
      encoding: args.encoding,
      model: args.model,
      safeHydrate(raw: Json): Model.Result<C> {
        return args.model.safeHydrate(raw);
      },
      probe(value: Json): boolean {
        return args.model.safeHydrate(value).isOk;
      },
    };
  }
}
